import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { InferType } from 'yup';

import {
  useAddTimeOffMutation,
  useEditTimeOffMutation,
  useTimeOffQuery,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting';
import { addTimeOffSchema } from '../schemas';
import dayjs from 'dayjs';

type IFormShape = InferType<typeof addTimeOffSchema>;

const initialValues: Partial<IFormShape> = {
  user_id: '',
  start_date: new Date().toISOString(),
  end_date: new Date().toISOString(),
  days: 1,
};

const addNewInitialValues: Partial<IFormShape> = {
  user_id: '',
  start_date: '',
  end_date: '',
  days: 0,
};

export const useAddTimeOff = (
  time_off_id: string,
  variant: 'create' | 'edit',
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const { mutateAsync: addTimeOffMutation, isPending: addIsPending } =
    useAddTimeOffMutation();
  const { mutateAsync: editTimeOffMutation, isPending: editIsPending } =
    useEditTimeOffMutation(time_off_id);

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(addTimeOffSchema),
    defaultValues: initialValues,
  });
  //passing the isOpen here to ensure that the fetches take place only when the modal is open
  const { data, isLoading } = useTimeOffQuery(time_off_id, isOpen);

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting, isValid, isDirty },
    getValues,
    reset,
  } = formMethods;

  const onStartEndDateChange = () => {
    const start = dayjs(getValues('start_date')).startOf('day');
    const end = dayjs(getValues('end_date')).startOf('day');

    const days = end.isBefore(start) ? 1 : end.diff(start, 'day') + 1;

    setValue('days', days, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  useEffect(() => {
    if (isOpen) {
      if (variant === 'edit' && data) {
        reset({
          ...data,
          start_date: dayjs(data.start_date).format('YYYY-MM-DD'),
          end_date: dayjs(data.end_date).format('YYYY-MM-DD'),
          days: data.days,
        });
      } else {
        reset({ ...addNewInitialValues });
      }
    }
  }, [data, isOpen]);

  const handleClose = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: IFormShape) => {
    const action =
      variant === 'edit' ? editTimeOffMutation : addTimeOffMutation;
    const successMessage =
      variant === 'create'
        ? 'You added a new PTO entry!'
        : 'PTO edited successfully';

    try {
      const start_date = values.start_date
        ? dayjs(values.start_date).format('YYYY-MM-DD')
        : '';
      const end_date = values.end_date
        ? dayjs(values.end_date).format('YYYY-MM-DD')
        : '';

      await action({
        start_date,
        end_date,
        days: values?.days || 0,
        user_id: values.user_id ?? '',
      });
      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset({ ...initialValues });
      toast.success(successMessage);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset({ ...initialValues });
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  return {
    isOpen,
    handleClose,
    handleOpen,
    onSubmit,
    isSubmitting,
    isValid,
    formMethods,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isEditSubmitting: addIsPending || editIsPending,
    onStartEndDateChange,
    isLoading,
  };
};
